import { Loader2, Plus, RefreshCcw } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReviewForm from "../Components/ReviewForm";
import {
  approveAdminReviewAPI,
  getAdminReviewsAPI,
  rejectAdminReviewAPI,
} from "../services/apicollection";

function formatDateTime(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const ReviewManagementPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const controller = new AbortController();
  const fetchReviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAdminReviewsAPI({ signal: controller.signal });
      if (res?.status && Array.isArray(res.data)) {
        setReviews(res.data);
      } else {
        setReviews([]);
        setError(res?.message || "Failed to load reviews");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load reviews");
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
    return () => {
      controller.abort();
    };
  }, [fetchReviews]);

  const handleSave = () => {
    setShowForm(false);
    setPage(1);
    fetchReviews();
  };

  const handleCancel = () => {
    setShowForm(false);
  };

  const sortedReviews = useMemo(() => {
    const copy = Array.isArray(reviews) ? [...reviews] : [];
    copy.sort((a, b) => {
      const at = new Date(a?.createdAt || 0).getTime();
      const bt = new Date(b?.createdAt || 0).getTime();
      return bt - at;
    });
    return copy;
  }, [reviews]);

  const total = sortedReviews.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const endIndexExclusive = Math.min(startIndex + pageSize, total);
  const pageRows = useMemo(
    () => sortedReviews.slice(startIndex, endIndexExclusive),
    [sortedReviews, startIndex, endIndexExclusive],
  );

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const setApproval = async (review, approved) => {
    if (!review?._id) return;
    setUpdatingId(review._id);
    try {
      const res = approved
        ? await approveAdminReviewAPI(review._id)
        : await rejectAdminReviewAPI(review._id);

      if (!res?.status) {
        alert(res?.message || "Failed to update review");
        return;
      }

      setReviews((prev) =>
        prev.map((r) =>
          r?._id === review._id ? { ...r, approved: !!approved } : r,
        ),
      );
    } finally {
      setUpdatingId(null);
    }
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <ReviewForm closeForm={handleSave} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-200">Reviews</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => fetchReviews()}
            className="inline-flex items-center gap-2 rounded-lg bg-[#0f1c2e] px-3 py-2 text-sm font-medium text-slate-200 ring-1 ring-slate-200 transition-colors hover:bg-[#0d1b2e]"
            title="Refresh"
          >
            <RefreshCcw size={16} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700"
          >
            <Plus size={16} />
            Add Review
          </button>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12">
          <Loader2 size={28} className="animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-center text-sm text-red-700">
          {error}
        </div>
      ) : sortedReviews.length === 0 ? (
        <div className="rounded-lg border border-[#1d3350] bg-[#0f1c2e] px-4 py-10 text-center text-sm text-slate-400">
          No reviews yet
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
            <div>
              Showing{" "}
              <span className="font-semibold text-slate-200">
                {startIndex + 1}–{endIndexExclusive}
              </span>{" "}
              of <span className="font-semibold text-slate-200">{total}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
                className="rounded-md bg-[#0f1c2e] px-2 py-1 ring-1 ring-slate-200 hover:bg-[#0d1b2e] disabled:opacity-60"
              >
                Prev
              </button>
              <span className="tabular-nums">
                Page <span className="font-semibold">{safePage}</span> /{" "}
                <span className="font-semibold">{totalPages}</span>
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                className="rounded-md bg-[#0f1c2e] px-2 py-1 ring-1 ring-slate-200 hover:bg-[#0d1b2e] disabled:opacity-60"
              >
                Next
              </button>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-2xl bg-[#0f1c2e] shadow-[0_20px_50px_rgba(0,0,0,0.08)]">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left">
                <thead className="border-b border-[#1d3350] bg-[#0d1b2e]">
                  <tr>
                    <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">
                      Student
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">
                      Course
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">
                      Rating
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">
                      Message
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">
                      Created
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pageRows.map((r) => {
                    const busy = updatingId === r?._id;
                    const approved = !!r?.approved;
                    return (
                      <tr
                        key={r?._id}
                        className="transition-colors hover:bg-[#0d1b2e]"
                      >
                        <td className="px-6 py-4 text-sm text-slate-200 whitespace-nowrap">
                          {r?.name || "—"}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap capitalize">
                          {r?.course || "—"}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">
                          {typeof r?.rating === "number" ? r.rating : "—"}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-300 max-w-[520px]">
                          <div className="line-clamp-2">{r?.message || "—"}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">
                          {formatDateTime(r?.createdAt)}
                        </td>
                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                          <span
                            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                              approved
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-rose-50 text-rose-700"
                            }`}
                          >
                            {approved ? "Approved" : "Rejected"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                          <div className="inline-flex overflow-hidden rounded-lg border border-[#1d3350]">
                            <button
                              type="button"
                              onClick={() => setApproval(r, true)}
                              disabled={busy || approved}
                              className="px-3 py-2 text-xs font-medium hover:bg-emerald-50 disabled:opacity-60"
                            >
                              {busy && !approved ? (
                                <span className="inline-flex items-center gap-1">
                                  <Loader2 size={14} className="animate-spin" />
                                  Updating
                                </span>
                              ) : (
                                "Approve"
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => setApproval(r, false)}
                              disabled={busy || !approved}
                              className="px-3 py-2 text-xs font-medium hover:bg-rose-50 disabled:opacity-60"
                            >
                              {busy && approved ? (
                                <span className="inline-flex items-center gap-1">
                                  <Loader2 size={14} className="animate-spin" />
                                  Updating
                                </span>
                              ) : (
                                "Reject"
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewManagementPage;
